/**
 * Egg + TM move pool resolver for the rare-pull mechanic (Pokédex Step 4).
 *
 * Pulls the Gen-3 learnset for the given species from @pkmn/dex and
 * filters to entries flagged 'E' (egg) or 'T' (TM). Returns a stable-
 * ordered list (the @pkmn/dex iteration order is what locks the pick
 * index against the determinism CI gate — bumping @pkmn/dex without
 * re-recording the snapshot is the failure mode TODO #13 calls out).
 *
 * Defensive: when the dex returns an empty list (content gap or future
 * bump that strips the egg/TM annotations), falls back to the curated
 * FALLBACK_EGG_MOVES table for the v0.1 species set. If both come back
 * empty, the caller's `pool.length > 0` check downgrades the rare pull
 * to a no-op offer (silent absence per design — no ✨, no SFX, no
 * eggPullMove).
 */

import { loadGen3 } from './dex';
import { FALLBACK_EGG_MOVES } from './eggMoves.fallback';

/**
 * Returns the union of Gen-3 egg + TM moves for `species`, lowercase
 * @pkmn move ids. Empty array on content gap or load failure (the
 * fallback table is consulted first; only species missing from both
 * the dex AND the fallback yield []).
 */
export async function getEggAndTmMoves(species: string): Promise<string[]> {
  try {
    const gen3 = await loadGen3();
    const learnset = await gen3.learnsets.get(species);
    if (learnset?.learnset) {
      const out: string[] = [];
      for (const [moveId, sources] of Object.entries(learnset.learnset)) {
        for (const src of sources) {
          // Gen-3 sources are 2-3 char strings: '3L<n>' (level-up),
          // '3E' (egg), '3T' (TM tutor), '3M' (HM-machine tutor).
          // Pick up E and T as "outside the level-up set" — these are
          // the moves a Pokémon could acquire via breeding or via a TM.
          if (src.length < 2 || src[0] !== '3') continue;
          const tag = src[1];
          if (tag === 'E' || tag === 'T') {
            out.push(moveId);
            break; // any matching source qualifies the move once
          }
        }
      }
      if (out.length > 0) return out;
    }
  } catch (err) {
    console.warn(`eggMoves: dex lookup failed for '${species}':`, err);
  }
  // Fall back to the curated table when the dex returns nothing
  // (defensive path g).
  const fallback = FALLBACK_EGG_MOVES[species];
  return fallback ? [...fallback] : [];
}
